import React, { useMemo } from "react";
import styled from "styled-components";

import SvgLoadingAnimation from "../../../components/loading";
import Avatar from "../../../components/avatar/Avatar";
import StatusBadge from "../../../components/status-badge/StatusBadge";
import DateDisplay, {
  DATE_DISPLAY_TYPE,
} from "../../../components/date-display/DateDisplay";
import {
  useGetNoteChangesQuery,
  type NoteChangeStatus,
  type NoteChangeSummary,
  type NoteSection,
} from "../../../app/services/notes";

/**
 * セクションのプルリクエスト（変更提案）一覧。
 *
 * - レビュー待ち（open）→ 履歴（merged / rejected）の順にグループ表示する
 * - 下書き（draft）は提出前のためここには出さない
 *   （サイドバーのツリーに「下書き」行として表示し、そこから編集・提出する）
 * - メンテナー以上は全 open が見え、コントリビューターは自分の分のみ
 *   （可視性は BE 判定）
 */

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 24px;
  /* NoteView と同じく本文幅を 720px にし、左右の余白は画面が狭いときだけ取る */
  width: min(720px, 100% - 32px);
  margin: 0 auto;
  padding: 24px 0 96px;
`;

const GroupTitle = styled.h3.attrs({
  className: "heading-13 u-line-height-150",
})`
  margin: 0;
  color: var(--soremo-light-gray);
`;

const ChangeRow = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px;
  border: 1px solid var(--soremo-border);
  border-radius: 8px;
  background: white;
  cursor: pointer;
  text-align: left;
  transition: background-color 0.15s ease;

  .change-main {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .change-title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--soremo-gray);
  }

  .change-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--soremo-light-gray);
  }

  .change-chevron {
    font-size: 20px;
    color: var(--soremo-border);
    flex-shrink: 0;
  }

  @media (hover: hover) {
    &:hover {
      background: var(--soremo-background);

      .change-chevron {
        color: var(--soremo-light-gray);
      }
    }
  }
`;

export const CHANGE_STATUS_LABELS: Record<
  NoteChangeStatus,
  { label: string; icon: string }
> = {
  draft: { label: "下書き", icon: "edit_note" },
  open: { label: "レビュー待ち", icon: "schedule" },
  merged: { label: "マージ済み", icon: "check_circle" },
  rejected: { label: "却下", icon: "block" },
};

const ChangeListItem: React.FC<{
  change: NoteChangeSummary;
  onOpen: (id: string) => void;
}> = ({ change, onOpen }) => (
  <ChangeRow onClick={() => onOpen(change.id)}>
    <Avatar src={change.author?.avatar ?? undefined} size={32} />
    <span className="change-main">
      <span className="change-title bodytext-13 u-line-height-150">
        {change.title}
      </span>
      <span className="change-meta bodytext-11">
        <span>{change.author?.display_name}</span>
        <DateDisplay
          date={change.modified ?? change.created ?? ""}
          type={DATE_DISPLAY_TYPE.DATE_ONLY}
        />
        <StatusBadge
          label={CHANGE_STATUS_LABELS[change.status].label}
          size="small"
        />
        <span>
          {change.target_type === "card" ? "カード" : "ノート"}
          {change.kind === "create" ? "の新規作成" : "の更新"}
        </span>
      </span>
    </span>
    <span className="change-chevron material-symbols-rounded">
      chevron_right
    </span>
  </ChangeRow>
);

export interface ChangesListViewProps {
  section: NoteSection;
  onOpenChange: (changeId: string) => void;
}

const ChangesListView: React.FC<ChangesListViewProps> = ({
  section,
  onOpenChange,
}) => {
  const { data, isLoading } = useGetNoteChangesQuery({
    section_id: section.id,
  });

  const groups = useMemo(() => {
    const changes = data?.data?.changes ?? [];
    return {
      open: changes.filter((c) => c.status === "open"),
      closed: changes.filter(
        (c) => c.status === "merged" || c.status === "rejected",
      ),
    };
  }, [data]);

  if (isLoading) return <SvgLoadingAnimation />;

  const isEmpty = groups.open.length === 0 && groups.closed.length === 0;

  return (
    <ListWrapper>
      <div className="u-col u-gap4">
        <h2 className="heading-spacing-18 u-line-height-150">
          プルリクエスト
        </h2>
        <p className="bodytext-11 u-text-light-gray">
          {section.title} への変更提案。承認されると公開内容に反映されます。
        </p>
      </div>

      {isEmpty && (
        <p className="bodytext-13 u-text-light-gray">
          進行中の変更提案はありません
        </p>
      )}

      {groups.open.length > 0 && (
        <div className="u-col u-gap8" style={{ alignItems: "stretch" }}>
          <GroupTitle>レビュー待ち</GroupTitle>
          {groups.open.map((c) => (
            <ChangeListItem key={c.id} change={c} onOpen={onOpenChange} />
          ))}
        </div>
      )}

      {groups.closed.length > 0 && (
        <div className="u-col u-gap8" style={{ alignItems: "stretch" }}>
          <GroupTitle>履歴</GroupTitle>
          {groups.closed.map((c) => (
            <ChangeListItem key={c.id} change={c} onOpen={onOpenChange} />
          ))}
        </div>
      )}
    </ListWrapper>
  );
};

export default ChangesListView;
